/**
 * 주소 검색 + 좌표 획득(`#867` 설계 §2, 요구사항 4) — 다음(Daum) 우편번호 팝업 + 카카오맵 JS SDK
 * Geocoder 를 조합한다. 모바일 앱이 이미 실사용 중인 조합(`search-kakako-address.html`)을 이 저장소
 * 번들 안에 직접 이식한 것이다 — 새 계정·키 발급 없이 기존 발급 키(`VITE_KAKAO_MAP_APP_KEY`)를
 * 재사용한다(설계 §2 자율 결정 9).
 *
 * 🚨 두 스크립트 모두 전역(`window`)에 얹히는 외부 SDK 라 이 저장소에 대응 `@types` 패키지가 없다 —
 * 실제로 쓰는 메서드만 최소로 앰비언트 선언한다(아래).
 */

declare global {
  interface Window {
    daum?: {
      Postcode: new (options: { oncomplete: (data: DaumPostcodeResult) => void; onclose?: () => void }) => {
        open: () => void
      }
    }
    kakao?: {
      maps: {
        load: (callback: () => void) => void
        services: {
          Geocoder: new () => {
            addressSearch: (
              address: string,
              callback: (result: KakaoGeocoderResult[], status: string) => void
            ) => void
          }
          Status: { OK: string }
        }
      }
    }
  }
}

interface DaumPostcodeResult {
  address: string
  roadAddress?: string
  zonecode?: string
}

interface KakaoGeocoderResult {
  x: string
  y: string
}

const DAUM_POSTCODE_SRC = 'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js'
const KAKAO_MAPS_SRC_BASE = 'https://dapi.kakao.com/v2/maps/sdk.js'

const scriptPromises = new Map<string, Promise<void>>()

/** 같은 `src` 스크립트를 중복 로드하지 않는다 — 여러 번 호출해도 `<script>` 는 1회만 삽입된다. */
function loadScriptOnce(src: string): Promise<void> {
  const cached = scriptPromises.get(src)
  if (cached) return cached

  const promise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`)
    if (existing) {
      resolve()
      return
    }
    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.onload = () => {
      resolve()
    }
    script.onerror = () => {
      scriptPromises.delete(src)
      reject(new VenueAddressSearchError('주소 검색 스크립트를 불러오지 못했습니다.'))
    }
    document.head.appendChild(script)
  })

  scriptPromises.set(src, promise)
  return promise
}

export class VenueAddressSearchError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'VenueAddressSearchError'
  }
}

export interface VenueAddressSearchResult {
  address: string
  latitude: number
  longitude: number
}

/**
 * 다음 우편번호 팝업을 열어 주소를 선택받고, 카카오 Geocoder 로 좌표까지 얻어 함께 반환한다.
 * 🚨 사용자가 팝업을 닫아 주소를 고르지 않으면 **resolve/reject 어느 쪽도 하지 않는다** — 페이지가
 * 다시 버튼을 누를 수 있는 상태로 남는다(팝업이 스스로 닫히므로 이 프라미스만 조용히 버려진다).
 */
export async function openVenueAddressSearch(): Promise<VenueAddressSearchResult> {
  const appKey = import.meta.env.VITE_KAKAO_MAP_APP_KEY?.trim()
  if (!appKey) {
    throw new VenueAddressSearchError('주소 검색이 설정되지 않았습니다.')
  }

  await loadScriptOnce(DAUM_POSTCODE_SRC)
  await loadScriptOnce(`${KAKAO_MAPS_SRC_BASE}?appkey=${appKey}&libraries=services&autoload=false`)

  const daum = window.daum
  const kakao = window.kakao
  if (!daum || !kakao) {
    throw new VenueAddressSearchError('주소 검색을 불러오지 못했습니다.')
  }

  return new Promise<VenueAddressSearchResult>((resolve, reject) => {
    new daum.Postcode({
      oncomplete: (data) => {
        const address = data.roadAddress || data.address
        kakao.maps.load(() => {
          const geocoder = new kakao.maps.services.Geocoder()
          geocoder.addressSearch(address, (result, status) => {
            if (status !== kakao.maps.services.Status.OK || !result[0]) {
              reject(new VenueAddressSearchError('선택한 주소의 좌표를 확인하지 못했습니다.'))
              return
            }
            resolve({ address, latitude: Number(result[0].y), longitude: Number(result[0].x) })
          })
        })
      },
    }).open()
  })
}
